import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dfh-corp-sites/ui'
import { AU, CA, US } from 'country-flag-icons/react/3x2'
import { navigate } from 'gatsby'
import React from 'react'

import useSiteSettings from '../../../../hooks/useSiteSettings'
import { usePageUrl } from '../../../../lib/pages/hooks/usePageUrl'
import { normalizeLink } from '../../../../lib/pages/links'
import { langCodes } from '../../../../utils/siteSettings'

const countryOptions = [
  {
    flag: 'usa',
    href: 'https://www.designsforhealth.com',
    lang: langCodes.en_US,
    title: 'United States (English)',
  },
  {
    flag: 'canada',
    href: 'https://www.designsforhealth.ca',
    lang: langCodes.en_CA,
    title: 'Canada (English)',
  },
  {
    flag: 'australia',
    href: 'https://www.designsforhealth.com.au',
    lang: langCodes.en_AU,
    title: 'Australia (English)',
  },
]

export interface FlagProps {
  className?: string
  country: string
}

const Flag: React.FC<FlagProps> = ({ className, country }) =>
  country === 'usa' ? (
    <US className={className} />
  ) : country === 'canada' ? (
    <CA className={className} />
  ) : country === 'australia' ? (
    <AU className={className} />
  ) : (
    <div className={className} />
  )

export interface FooterCountrySelectorProps {
  className?: string
}

export const FooterCountrySelector: React.FC<FooterCountrySelectorProps> = () => {
  const { lang } = useSiteSettings() || {}
  const { getPagePath } = usePageUrl()
  const staticSelectId = 'footer-country-select-content'
  const defaultFlag = countryOptions.find((country) => country.lang === lang)?.flag || 'usa'

  return (
    <nav
      className="w-72 ml-[-12px] mt-5 xl:mt-0 xl:justify-self-end"
      aria-label="Country & Language"
    >
      <label id="country-selector" className="font-semibold pl-3 text-sm">
        Country & Language
      </label>

      {/* Hidden container to satisfy aria-controls validation */}
      <div id={staticSelectId} style={{ display: 'none' }} aria-hidden="true" />

      <Select
        defaultValue={lang || langCodes.en_US}
        onValueChange={(value) => {
          if (typeof window !== 'undefined') {
            const selected = countryOptions.find((country) => country.lang === value)
            if (selected) {
              const link = normalizeLink(selected.href, {})
              const finalUrl = link.type === 'gatsby' ? getPagePath(link.to) : link.href
              navigate(finalUrl || '/')
            }
          }
        }}
      >
        <SelectTrigger
          className="border-transparent"
          aria-labelledby="country-selector"
          aria-controls={staticSelectId}
        >
          <SelectValue>
            <div className="flex items-center gap-3">
              <Flag country={defaultFlag} className="size-6" />
              <span className="font-semibold text-base">
                {countryOptions.find((country) => country.lang === lang)?.title}
              </span>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent id={staticSelectId} className="w-full rounded-none bg-white text-brand-navy">
          <SelectGroup>
            {countryOptions.map((country) => (
              <SelectItem
                className="data-[highlighted]:bg-brand-orange data-[highlighted]:text-white"
                key={country.lang}
                value={country.lang}
              >
                <span className="flex items-center gap-3 w-full">
                  <Flag country={country.flag} className="size-6" />
                  <span className="font-semibold text-base">{country.title}</span>
                </span>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </nav>
  )
}
